import type { ImageStore } from 'visualizations/image-store'
import type { SettingsInterface } from './types'
import { Node } from './node'
import 'leaflet/dist/leaflet.css'
import * as L from 'leaflet'
import * as PIXI from 'pixi.js'
import 'leaflet-pixi-overlay'

export class GeoMap {
  private readonly imageStore: ImageStore
  private eventListeners: { [key: string]: Array<(...args: any[]) => void> } = {}
  private readonly map: L.Map

  constructor (element: HTMLElement, settings: SettingsInterface, nodes: any, imageStore: ImageStore) {
    PIXI.Graphics.curves.adaptive = false

    this.imageStore = imageStore
    this.nodes = this.initializeNodes(nodes)

    this.map = L.map(element, { renderer: L.canvas() }).setView([settings.initialLatitude, settings.initialLongitude], settings.initialScale)
    this.map.attributionControl.setPrefix('')

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map)

    this.pixiContainer = new PIXI.Container()

    this.pixiOverlay = L.pixiOverlay((utils) => {
      this.container = utils.getContainer()
      this.renderer = utils.getRenderer()
      this.project = utils.latLngToLayerPoint
      this.scale = utils.getScale()

      this.container.removeChildren()

      const minimumRadius = 0.1
      const calculatedRadius = 5 / this.scale
      const radius = calculatedRadius > minimumRadius ? calculatedRadius : minimumRadius

      this.nodes.forEach(node => {
        node.initializeGraphics(this.project, this.container, () => this.click(node), radius)
      })

      this.renderer.render(this.container)
    }, this.pixiContainer)

    this.pixiOverlay.addTo(this.map)

    this.map.on('click', (event) => this.handleMapClick(event))
  }

  private initializeNodes (nodes: any): Node[] {
    return nodes.map((node: any) => new Node(node, this))
  }

  public on (event: string, listener: (...args: any[]) => void): void {
    if (this.eventListeners[event] === undefined) {
      this.eventListeners[event] = []
    }
    this.eventListeners[event].push(listener)
  }

  private emit (event: string, ...args: any[]): void {
    if (this.eventListeners[event] !== undefined) {
      this.eventListeners[event].forEach(listener => listener(...args))
    }
  }

  private click (node: Node): void {
    // TODO this.highlight(node)
    this.emit('click', node.id)
  }

  private unclick (): void {
    this.emit('unclick')
  }

  private handleMapClick (event: L.LeafletMouseEvent): void {
    if (event.originalEvent.target.classList.contains('leaflet-interactive') === false) {
      this.unclick()
    }
  }
}
